import { useInfiniteQuery, useQuery } from 'react-query'
import { type FiltersDomain } from '../../../../types/domain/filters-domain'
import { type MeetingDashboardListDomain } from '../../../../types/domain/meeting-dashboard-list-domain'
import type PaginationDomain from '../../../../types/domain/pagination-domain'
import { getValidFilters } from '../../../../utils/filters'
import { getQueryStrinfied } from '../../../../utils/query'
import { api } from '../../../api'

type PeriodDomain = {
  startDate: string
  endDate: string
}

interface MeetingsCount {
  pendingMeetings: number
  concludedMeetings: number
  canceledMeetings: number
  totalMeetings: number
}

export const fetchMeetings = async (filters: FiltersDomain) => {
  const queryParams = getQueryStrinfied(getValidFilters(filters))
  const response = await api.get<PaginationDomain<MeetingDashboardListDomain>>(
    `/meetings/per-expert?&${queryParams}`
  )

  return response.data
}

export const getTotalMeetings = async (filters: PeriodDomain) => {
  const response = await api.get<MeetingsCount>(
    `/meetings/dashboard/count?startDate=${filters.startDate}&endDate=${filters.endDate}`
  )

  return response.data
}

export const fetchExpertMeetings = async (filters: FiltersDomain) => {
  const queryParams = getQueryStrinfied(getValidFilters(filters))
  const response = await api.get<PaginationDomain<MeetingDashboardListDomain>>(
    `/meetings/dashboard/expert-meetings?${queryParams}`
  )

  return response.data
}

export const fetchResearcherMeetings = async (filters: FiltersDomain) => {
  const queryParams = getQueryStrinfied(getValidFilters(filters))
  const response = await api.get<PaginationDomain<MeetingDashboardListDomain>>(
    `/meetings/dashboard/researcher-meetings?${queryParams}`
  )

  return response.data
}

export const fetchAssetAnalystMeetings = async (filters: FiltersDomain) => {
  const queryParams = getQueryStrinfied(getValidFilters(filters))
  const response = await api.get<PaginationDomain<MeetingDashboardListDomain>>(
    `/meetings/dashboard/asset-analyst-meetings?${queryParams}`
  )

  return response.data
}

export const fetchAssetMeetings = async (filters: FiltersDomain) => {
  const queryParams = getQueryStrinfied(getValidFilters(filters))
  const response = await api.get<PaginationDomain<MeetingDashboardListDomain>>(
    `/meetings/dashboard/asset-meetings?${queryParams}`
  )

  return response.data
}

export const fetchClientMeetings = async (filters: FiltersDomain) => {
  const queryParams = getQueryStrinfied(getValidFilters(filters))
  const response = await api.get<PaginationDomain<MeetingDashboardListDomain>>(
    `/meetings/dashboard/client-meetings?${queryParams}`
  )

  return response.data
}

export const useMeetingsOnDashboardQuery = (filters: FiltersDomain) => {
  return useQuery<PaginationDomain<MeetingDashboardListDomain>>(
    ['meetings', filters],
    () => fetchMeetings(filters),
    { staleTime: 1000 * 60 * 60 * 1, keepPreviousData: true } // 1 hour
  )
}

export const useInfiniteMeetingsOnDashboardQuery = (filters: any) => {
  return useInfiniteQuery(
    ['meetings'],
    ({ pageParam = 1 }) => fetchMeetings({ ...filters, page: pageParam }),
    {
      getNextPageParam ({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}

export const useMeetingsCountOnDashboardQuery = (filters: PeriodDomain) => {
  return useQuery<MeetingsCount>(
    ['meetings', filters],
    () => getTotalMeetings(filters),
    { staleTime: 1000 * 60 * 60 * 1, keepPreviousData: true } // 1 hour
  )
}

export const useInfiniteExpertMeetingsQuery = (filters: any) => {
  return useInfiniteQuery(
    ['meetings-expert'],
    ({ pageParam = 1 }) => fetchExpertMeetings({ ...filters, page: pageParam }),
    {
      getNextPageParam ({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}

export const useInfiniteResearcherMeetingsQuery = (filters: any) => {
  return useInfiniteQuery(
    ['meetings-researcher'],
    ({ pageParam = 1 }) => fetchResearcherMeetings({ ...filters, page: pageParam }),
    {
      getNextPageParam ({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}

export const useInfiniteAssetAnalystMeetingsQuery = (filters: any) => {
  return useInfiniteQuery(
    ['meetings-asset-analyst'],
    ({ pageParam = 1 }) => fetchAssetAnalystMeetings({ ...filters, page: pageParam }),
    {
      getNextPageParam ({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}

export const useInfiniteAssetMeetingsQuery = (filters: any) => {
  return useInfiniteQuery(
    ['meetings-asset'],
    ({ pageParam = 1 }) => fetchAssetMeetings({ ...filters, page: pageParam }),
    {
      getNextPageParam ({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}

export const useInfiniteClientMeetingsQuery = (filters: any) => {
  return useInfiniteQuery(
    ['meetings-client'],
    ({ pageParam = 1 }) => fetchClientMeetings({ ...filters, page: pageParam }),
    {
      getNextPageParam ({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}
